import * as R from "remeda";
import { dset } from 'dset';
import { ChatHandlerConfig, useGetApiNotificationSettingsList } from "@/API";
import { ActionIcon, Autocomplete, Button, Divider, Group, Modal, Paper, Select, Stack, Switch, Tabs, TagsInput, Text, Textarea, TextInput, Tooltip } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { PropsWithChildren, useEffect, useState } from "react";
import { useImmer } from "use-immer";
import { IconArrowMoveLeft, IconArrowMoveRight, IconBellPlus, IconFilePlus, IconSwipeLeftFilled, IconSwipeRightFilled, IconTrash } from "@tabler/icons-react";
import { modals } from "@mantine/modals";

export function ChatHandlerConfigEditBotton(props: { workspaceId: string, value?: ChatHandlerConfig[], onChange?: (value: ChatHandlerConfig[]) => void } & PropsWithChildren) {
    const [opened, { open, close }] = useDisclosure(false);
    const [activeTab, setActiveTab] = useState<string | null>('0');
    const [data, setData] = useImmer<ChatHandlerConfig[]>([])
    const notificationList = useGetApiNotificationSettingsList({ workspaceId: props.workspaceId })
    useEffect(() => {
        if (opened) {
            if (props.value) setData(props.value)
            else setData([])
        }
    }, [opened])

    return <>
        <div onClick={open}>
            {props.children}
        </div>
        <Modal opened={opened} onClose={close} title="聊天處理" closeOnClickOutside={false} size="xl">
            <Group gap={5}>
                <Tooltip label="新增">
                    <ActionIcon variant="subtle" onClick={() => {
                        setActiveTab(data.length.toString())
                        setData(x => {
                            x.push({

                            })
                        })
                    }}>
                        <IconFilePlus />
                    </ActionIcon >
                </Tooltip>
                <Tooltip label="刪除">
                    <ActionIcon variant="subtle" color="red" onClick={() => {
                        var idx = Number(activeTab)
                        modals.openConfirmModal({
                            title: '刪除設定',
                            centered: true,
                            children: (
                                <Text size="sm">
                                    是否要刪除 {data[idx].name} 設定
                                </Text>
                            ),
                            labels: { confirm: '刪除', cancel: "取消" },
                            confirmProps: { color: 'red' },
                            onConfirm: () => {
                                setActiveTab(R.clamp(idx, { min: 0, max: data.length - 2 }).toString())
                                setData(x => {
                                    x = x.splice(idx, 1)
                                })
                            },
                        });
                    }}>
                        <IconTrash />
                    </ActionIcon >
                </Tooltip>
                <Tooltip label="向前交換">
                    <ActionIcon variant="subtle" onClick={() => {
                        var idx = Number(activeTab)
                        if (idx - 1 >= 0) {
                            setData(R.swapIndices(idx, idx - 1))
                            setActiveTab((idx - 1).toString())
                        }
                    }}>
                        <IconSwipeLeftFilled />
                    </ActionIcon >
                </Tooltip>
                <Tooltip label="向後交換">
                    <ActionIcon variant="subtle" onClick={() => {
                        var idx = Number(activeTab)
                        if (idx + 1 < data.length) {
                            setData(R.swapIndices(idx, idx + 1))
                            setActiveTab((idx + 1).toString())
                        }
                    }}>
                        <IconSwipeRightFilled />
                    </ActionIcon >
                </Tooltip>
            </Group>
            <Tabs value={activeTab} onChange={setActiveTab}>
                <Tabs.List>
                    {data.map((x, idx) => <Tabs.Tab key={idx} value={idx.toString()}>{x.name ? x.name : "新設定"}</Tabs.Tab>)}
                </Tabs.List>
                {data.map((_, idx) => <Tabs.Panel key={idx} value={idx.toString()}>
                    <Stack key={idx} gap={5} mt={5}>
                        <TextInput
                            label="名稱"
                            value={data[idx].name}
                            onChange={v => {
                                setData(x => {
                                    x[idx].name = v.target.value
                                })
                            }}
                        />
                        <Divider my="md" />
                        <Text size="xl" fw={600}>
                            條件
                        </Text>
                        <TextInput
                            label="Regex"
                            description="符合此條件才會觸發動作"
                            value={data[idx].condition?.regexConditions?.at(0)?.regex ?? ""}
                            onChange={v => {
                                setData(x => {
                                    dset(x[idx], `condition.regexConditions.0.regex`, v.target.value)
                                })
                            }}
                        />
                        <Divider my="md" />
                        <Text size="xl" fw={600}>
                            動作
                        </Text>
                        <TagsInput
                            label="Tags"
                            description="為聊天室添加Tag"
                            value={data[idx].action?.botProviderAction?.tags}
                            onChange={(v) => {
                                setData(x => {
                                    dset(x[idx], `action.botProviderAction.tags`, v)
                                })
                            }} />
                        <Switch
                            label="是否關閉自動回應"
                            description="啟用後，之後都不會再使用AI自動回應使用者直到管理員重新設定"
                            checked={!!data[idx].action?.botProviderAction?.disableReply}
                            onChange={(v) => {
                                setData(x => {
                                    dset(x[idx], `action.botProviderAction.disableReply`, v.target.checked)
                                })
                            }} />
                        <Textarea
                            label="手動設定本次回應內容"
                            description="留空表示不設定"
                            placeholder="不設定"
                            value={data[idx].action?.chatAction?.replyMessage?.message}
                            onChange={(v) => {
                                setData(x => {
                                    dset(x[idx], `action.chatAction.replyMessage.message`, v.target.value)
                                })
                            }}
                        />
                        <Group gap={5}>
                            <Text fw={500}>
                                通知
                            </Text>
                            <ActionIcon variant="subtle" onClick={() => {
                                var url = window.origin + "/workspace/${{WorkspaceId}}/botProvider/${{BotProviderId}}/user/${{UserId}}"
                                setData(x => {
                                    x[idx].action?.notificationActions?.push({
                                        message: `平台:$\{\{Provider\}\}\n使用者回應:$\{\{UserMessage\}\}\n紀錄網址:${url}`
                                    })
                                })
                            }}>
                                <IconBellPlus />
                            </ActionIcon>
                        </Group>
                        {
                            data[idx].action?.notificationActions?.map((notification, nidx) => <Paper key={nidx} withBorder pos="relative" shadow="xs" p="md">
                                <Select
                                    label="通知設定"
                                    data={notificationList.data?.data.filter(x => !!x.id).map(x => {
                                        return {
                                            label: (x.name ?? "") + `(${x.id})`,
                                            value: x.id!
                                        }
                                    })}
                                    value={notification.notificationSettingsId}
                                    onChange={v => {
                                        setData(x => {
                                            dset(x[idx], `action.notificationActions.${nidx}.notificationSettingsId`, v)
                                        })
                                    }}
                                />
                                <Textarea
                                    label="通知文字"
                                    value={notification.message}
                                    autosize
                                    onChange={v => {
                                        setData(x => {
                                            dset(x[idx], `action.notificationActions.${nidx}.message`, v.target.value)
                                        })
                                    }}
                                />
                                <Button color="red" onClick={() => {
                                    setData(x => {
                                        x[idx].action?.notificationActions?.splice(nidx, 1)
                                    })
                                }}>
                                    刪除
                                </Button>
                            </Paper>)
                        }
                    </Stack>
                </Tabs.Panel>)}
            </Tabs>

            <Group justify="flex-end" mt="md" onClick={() => {
                props.onChange?.(data)
            }}>
                <Button type="submit">保存</Button>
            </Group>
        </Modal>
    </>
}